import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import _ from 'lodash'
import { integrationsKeysUrl, slackApiMessagesUrl, oauthConnectionsUrl } from 'urls'
import { getAxiosDefaultConfig } from '../../modules/utils'
import {
  switchOrganization,
  switchToHomeWorkspace,
  createOrganization,
} from '../../features/organizations/organizationsSlice'

export const initialState = {
  currentChannel: null,
  currentChannelAccount: null,
  connectionModal: {
    visible: false,
  },
  backfillModal: {
    visible: false,
  },
  settingsModal: {
    visible: false,
  },
  csvModal: {
    visible: false,
  },
  inkfrogCsvModal: {
    visible: false,
  },
  inkfrogOrdersCsvModal: {
    visible: false,
  },
  /** Incremented after a successful Inkfrog CSV import so Integrations refetches `/stats`. */
  inkfrogListingsStatsNonce: 0,
  importOrdersModal: {
    visible: false,
  },
  /** Incremented after a successful Inkfrog orders CSV import so Integrations refetches order stats. */
  inkfrogOrdersStatsNonce: 0,
  /** Passed as `recent_imports_limit` to GET /inkfrog-orders/stats (10 default, 50 when expanded). */
  inkfrogOrdersRecentImportsLimit: 10,
  customToolsModal: {
    visible: false,
  },
  allPublicKeys: {},
  publicKeys: {},
  mcpPublicKeys: {},
  oauthConnections: {
    status: 'idle',
    data: [],
    error: null,
  },
}

const normalizeKey = (_key) => ({
  keyId: _key.key_id ?? _key.keyId,
  keyValue: _key.key_value ?? _key.keyValue,
  tags: _key.tags,
})

// Will match any keys whose key_id starts with ownerId
export const fetchAllPublicKeys = createAsyncThunk('integrations/fetchAllPublicKeys', async (payload) => {
  const { ownerId } = payload
  let res = await axios.get(`${integrationsKeysUrl}?keyId=${ownerId}`, getAxiosDefaultConfig())
  const raw = res?.data
  const keys = Array.isArray(raw) ? raw : Object.values(raw || {})
  return keys.map(normalizeKey).filter((k) => k.keyId != null && k.keyId !== '')
})

/** Fetch integration keys for owner + shared account IDs (used only for PayHelm MCP AI connected state). */
export const fetchMcpPublicKeys = createAsyncThunk('integrations/fetchMcpPublicKeys', async (payload) => {
  const { ownerId, sharedAccountIds = [], currentUserId, isTeamOwner } = payload
  // Owners: load MCP keys for the team (owner + each shared account). Invitees: only their own MCP row.
  const rawIds = isTeamOwner
    ? [ownerId, currentUserId, ...sharedAccountIds]
    : [currentUserId]
  const keyIds = _.uniq(rawIds.filter((id) => id != null && id !== ''))
  if (keyIds.length === 0) return []
  const params = { keyId: keyIds }
  let res = await axios.get(integrationsKeysUrl, { ...getAxiosDefaultConfig(), params })
  const raw = res?.data
  const keys = Array.isArray(raw) ? raw : Object.values(raw || {})
  const normalized = keys.map(normalizeKey).filter((k) => k.keyId != null && k.keyId !== '')
  const MCP_SUFFIXES = ['payhelm_ai_mcp', 'openai_connector']
  return normalized.filter((k) => MCP_SUFFIXES.some((s) => k.keyId && k.keyId.includes(s)))
})

export const createPublicKey = createAsyncThunk('integrations/createPublicKey', async (payload) => {
  const { keyId, keyValue } = payload
  let res = await axios.post(integrationsKeysUrl, payload, getAxiosDefaultConfig())
  return { keyId, keyValue }
})

export const updatePublicKey = createAsyncThunk('integrations/updatePublicKey', async (payload) => {
  const { keyId, keyValue } = payload
  let res = await axios.put(`${integrationsKeysUrl}/${keyId}`, payload, getAxiosDefaultConfig())
  return { keyId, keyValue }
})

export const deletePublicKey = createAsyncThunk('integrations/deletePublicKey', async (payload) => {
  const { keyId } = payload
  let res = await axios.delete(`${integrationsKeysUrl}/${keyId}`, getAxiosDefaultConfig())
  return { keyId }
})

export const postSlackMessage = createAsyncThunk('integrations/postSlackMessage', async (payload) => {
  const { channel, text } = payload
  let res = await axios.post(slackApiMessagesUrl, payload, getAxiosDefaultConfig())
  return { channel, text }
})

// OAuth Connections
export const fetchOAuthConnections = createAsyncThunk('integrations/fetchOAuthConnections', async () => {
  const res = await axios.get(oauthConnectionsUrl, getAxiosDefaultConfig())
  return res.data.connections
})

export const revokeOAuthConnection = createAsyncThunk('integrations/revokeOAuthConnection', async (payload) => {
  const { clientId } = payload
  await axios.delete(`${oauthConnectionsUrl}/${clientId}`, getAxiosDefaultConfig())
  return { clientId }
})

export const integrationsSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {
    setCurrentChannel: (state, action) => {
      state.currentChannel = action.payload
    },
    setCurrentChannelAccount: (state, action) => {
      state.currentChannelAccount = action.payload
    },
    openConnectionModal: (state, action) => {
      state.connectionModal.visible = true
    },
    closeConnectionModal: (state, action) => {
      state.connectionModal.visible = false
    },
    openBackfillModal: (state, action) => {
      state.backfillModal.visible = true
    },
    closeBackfillModal: (state, action) => {
      state.backfillModal.visible = false
    },
    openSettingsModal: (state, action) => {
      state.settingsModal.visible = true
    },
    closeSettingsModal: (state, action) => {
      state.settingsModal.visible = false
    },
    openCsvModal: (state, action) => {
      state.csvModal.visible = true
    },
    closeCsvModal: (state, action) => {
      state.csvModal.visible = false
    },
    openInkfrogCsvModal: (state, action) => {
      state.inkfrogCsvModal.visible = true
    },
    closeInkfrogCsvModal: (state, action) => {
      state.inkfrogCsvModal.visible = false
    },
    openInkfrogOrdersCsvModal: (state, action) => {
      state.inkfrogOrdersCsvModal.visible = true
    },
    closeInkfrogOrdersCsvModal: (state, action) => {
      state.inkfrogOrdersCsvModal.visible = false
    },
    bumpInkfrogListingsStatsNonce: (state) => {
      state.inkfrogListingsStatsNonce += 1
    },
    openImportOrdersModal: (state) => {
      state.importOrdersModal.visible = true
    },
    closeImportOrdersModal: (state) => {
      state.importOrdersModal.visible = false
    },
    bumpInkfrogOrdersStatsNonce: (state) => {
      state.inkfrogOrdersStatsNonce += 1
      state.inkfrogOrdersRecentImportsLimit = 10
    },
    expandInkfrogOrdersRecentImports: (state) => {
      state.inkfrogOrdersRecentImportsLimit = 50
    },
    collapseInkfrogOrdersRecentImports: (state) => {
      state.inkfrogOrdersRecentImportsLimit = 10
    },
    openCustomToolsModal: (state) => {
      state.customToolsModal.visible = true
    },
    closeCustomToolsModal: (state) => {
      state.customToolsModal.visible = false
    },
    resetIntegrationCredentials: (state) => {
      state.publicKeys = { status: 'idle', data: null, error: null }
      state.mcpPublicKeys = { status: 'idle', data: null, error: null }
    },
  },
  extraReducers(builder) {
    builder
      /**
       *
       * integrations keys api
       *
       */
      .addCase(fetchAllPublicKeys.pending, (state, action) => {
        state.publicKeys.status = 'loading'
      })
      .addCase(fetchAllPublicKeys.fulfilled, (state, action) => {
        state.publicKeys.data = action.payload
        state.publicKeys.status = 'succeeded'
      })
      .addCase(fetchAllPublicKeys.rejected, (state, action) => {
        state.publicKeys.status = 'failed'
        state.publicKeys.error = action.error.message
      })
      .addCase(createPublicKey.fulfilled, (state, action) => {
        state.publicKeys.data = [...(state.publicKeys.data || []), action.payload]
      })
      .addCase(updatePublicKey.fulfilled, (state, action) => {
        const data = state.publicKeys.data || []
        const { keyId } = action.meta.arg
        const index = _.findIndex(data, (key) => key.keyId === keyId)
        if (index >= 0) _.set(state.publicKeys.data, [index, 'keyValue'], action.payload.keyValue)
      })
      .addCase(deletePublicKey.fulfilled, (state, action) => {
        const { keyId } = action.meta.arg
        state.publicKeys.data = _.filter(state.publicKeys.data || [], (key) => key.keyId !== keyId)
      })

      /**
       * MCP keys (owner + shared accounts) for PayHelm MCP AI only
       */
      .addCase(fetchMcpPublicKeys.pending, (state) => {
        state.mcpPublicKeys.status = 'loading'
      })
      .addCase(fetchMcpPublicKeys.fulfilled, (state, action) => {
        state.mcpPublicKeys.data = action.payload
        state.mcpPublicKeys.status = 'succeeded'
      })
      .addCase(fetchMcpPublicKeys.rejected, (state, action) => {
        state.mcpPublicKeys.status = 'failed'
        state.mcpPublicKeys.error = action.error.message
      })

      /**
       *
       * post slack message
       *
       */
      .addCase(postSlackMessage.fulfilled, (state, action) => {
        const { channel, text } = action.meta.arg
      })

      /**
       *
       * OAuth connections
       *
       */
      .addCase(fetchOAuthConnections.pending, (state) => {
        state.oauthConnections.status = 'loading'
      })
      .addCase(fetchOAuthConnections.fulfilled, (state, action) => {
        state.oauthConnections.data = action.payload
        state.oauthConnections.status = 'succeeded'
      })
      .addCase(fetchOAuthConnections.rejected, (state, action) => {
        state.oauthConnections.status = 'failed'
        state.oauthConnections.error = action.error.message
      })
      .addCase(revokeOAuthConnection.fulfilled, (state, action) => {
        const { clientId } = action.payload
        state.oauthConnections.data = state.oauthConnections.data.filter(
          (conn) => conn.client_id !== clientId
        )
      })
      .addCase(switchOrganization.fulfilled, (state) => {
        state.publicKeys = { status: 'idle', data: null, error: null }
        state.mcpPublicKeys = { status: 'idle', data: null, error: null }
      })
      .addCase(switchToHomeWorkspace.fulfilled, (state) => {
        state.publicKeys = { status: 'idle', data: null, error: null }
        state.mcpPublicKeys = { status: 'idle', data: null, error: null }
      })
      .addCase(createOrganization.fulfilled, (state) => {
        state.publicKeys = { status: 'idle', data: null, error: null }
        state.mcpPublicKeys = { status: 'idle', data: null, error: null }
      })
  },
})

export const {
  setCurrentChannel,
  setCurrentChannelAccount,
  openConnectionModal,
  closeConnectionModal,
  openBackfillModal,
  closeBackfillModal,
  openSettingsModal,
  closeSettingsModal,
  openCsvModal,
  closeCsvModal,
  openInkfrogCsvModal,
  closeInkfrogCsvModal,
  openInkfrogOrdersCsvModal,
  closeInkfrogOrdersCsvModal,
  bumpInkfrogListingsStatsNonce,
  openImportOrdersModal,
  closeImportOrdersModal,
  bumpInkfrogOrdersStatsNonce,
  expandInkfrogOrdersRecentImports,
  collapseInkfrogOrdersRecentImports,
  openCustomToolsModal,
  closeCustomToolsModal,
  resetIntegrationCredentials,
} = integrationsSlice.actions

/**
 *
 * Selectors
 *
 */
export const selectCurrentChannel = (state) => state.integrations.currentChannel
export const selectCurrentChannelAccount = (state) => state.integrations.currentChannelAccount
export const selectConnectionModalVisible = (state) => state.integrations.connectionModal.visible
export const selectBackillModalVisible = (state) => state.integrations.backfillModal.visible
export const selectSettingsModalVisible = (state) => state.integrations.settingsModal.visible
export const selectCsvModalVisible = (state) => state.integrations.csvModal.visible
export const selectInkfrogCsvModalVisible = (state) => state.integrations.inkfrogCsvModal.visible
export const selectInkfrogOrdersCsvModalVisible = (state) => state.integrations.inkfrogOrdersCsvModal.visible
export const selectInkfrogListingsStatsNonce = (state) => state.integrations.inkfrogListingsStatsNonce
export const selectImportOrdersModalVisible = (state) => state.integrations.importOrdersModal.visible
export const selectInkfrogOrdersStatsNonce = (state) => state.integrations.inkfrogOrdersStatsNonce
export const selectInkfrogOrdersRecentImportsLimit = (state) =>
  state.integrations.inkfrogOrdersRecentImportsLimit ?? 10
export const selectCustomToolsModalVisible = (state) => state.integrations.customToolsModal.visible

export const selectAllPublicKeysStatus = (state) => state.integrations.publicKeys.status || 'idle'
export const selectAllPublicKeysError = (state) => state.integrations.publicKeys.error
export const selectAllPublicKeys = (state) => state.integrations.publicKeys.data

export const selectMcpPublicKeysStatus = (state) => state.integrations.mcpPublicKeys.status || 'idle'
export const selectMcpPublicKeys = (state) => state.integrations.mcpPublicKeys.data
export const selectOAuthConnectionsStatus = (state) => state.integrations.oauthConnections.status
export const selectOAuthConnectionsError = (state) => state.integrations.oauthConnections.error
export const selectOAuthConnections = (state) => state.integrations.oauthConnections.data

export const selectChannelPublicKeys = createSelector(
  [(state) => state.integrations.publicKeys, (state, channelId) => channelId],
  (publicKeys, channelId) => {
    return _.chain(publicKeys.data)
      .filter((key) => key.keyId.includes(channelId))
      .uniqWith((k1, k2) => k1.keyValue === k2.keyValue)
      .value()
  }
)

export const selectPublicKey = createSelector(
  [(state) => state.integrations.publicKeys, (state, keyId) => keyId],
  (publicKeys, keyId) => publicKeys.data.find((key) => key.keyId === keyId) || null
)
// export const selectChannelPublicKeysStatus = createSelector(
//   [(state) => state.integrations.publicKeys, (state, channelId) => channelId],
//   (publicKeys, channelId) => (publicKeys[channelId]?.status ? publicKeys[channelId].status : 'idle')
// )
// export const selectChannelPublicKeysError = createSelector(
//   [(state) => state.integrations.publicKeys, (state, channelId) => channelId],
//   (publicKeys, channelId) => (publicKeys[channelId] ? publicKeys[channelId].error : null)
// )
// export const selectChannelPublicKeys = createSelector(
//   [(state) => state.integrations.publicKeys, (state, channelId) => channelId],
//   (publicKeys, channelId) => (publicKeys[channelId] ? publicKeys[channelId].data : null)
// )
// export const selectPublicKey = createSelector(
//   [(state) => state.integrations.publicKeys, (state, keyId) => keyId],
//   (publicKeys, keyId) => {
//     const channelId = keyId.split('--')[1]
//     if (publicKeys[channelId]) {
//       return publicKeys[channelId].data.find((key) => key.keyId === keyId) || null
//     } else {
//       return null
//     }
//   }
// )

export default integrationsSlice.reducer
